// profiles.ts — Patch 010.A.4 R7 split. Patch 010.E.4 profile API.

import { request } from './request'
import type {
  CreateInternalRoleInput,
  CreateProfileInput,
  CreateRelationshipInput,
  PatchProfileInput,
  ProfileDetailResponse,
  ProfileInternalRole,
  ProfileListResponse,
  ProfileRelationship,
  ProfileSummary,
  ProjectProfilesResponse,
} from './types'

export const profilesApi = {
  list: (params?: { lifecycleStage?: string; search?: string; limit?: number; offset?: number }) => {
    const q: string[] = []
    if (params?.lifecycleStage) q.push(`lifecycle_stage=${encodeURIComponent(params.lifecycleStage)}`)
    if (params?.search) q.push(`search=${encodeURIComponent(params.search)}`)
    if (typeof params?.limit === 'number') q.push(`limit=${params.limit}`)
    if (typeof params?.offset === 'number') q.push(`offset=${params.offset}`)
    const qs = q.length > 0 ? `?${q.join('&')}` : ''
    return request<ProfileListResponse>(`/api/profiles${qs}`)
  },
  get: (profileId: string) =>
    request<ProfileDetailResponse>(`/api/profiles/${encodeURIComponent(profileId)}`),
  create: (body: CreateProfileInput) =>
    request<{ ok: true; profile: ProfileSummary; request_id: string }>(
      `/api/profiles`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  patch: (profileId: string, body: PatchProfileInput) =>
    request<{ ok: true; profile: ProfileSummary; request_id: string }>(
      `/api/profiles/${encodeURIComponent(profileId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  relationships: {
    create: (profileId: string, body: CreateRelationshipInput) =>
      request<{ ok: true; relationship: ProfileRelationship; request_id: string }>(
        `/api/profiles/${encodeURIComponent(profileId)}/relationships`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    end: (profileId: string, relationshipId: string) =>
      request<{ ok: true; ended_at: string; request_id: string }>(
        `/api/profiles/${encodeURIComponent(profileId)}/relationships/${encodeURIComponent(relationshipId)}`,
        { method: 'DELETE' },
      ),
  },
  internalRoles: {
    create: (profileId: string, body: CreateInternalRoleInput) =>
      request<{ ok: true; internal_role: ProfileInternalRole; request_id: string }>(
        `/api/profiles/${encodeURIComponent(profileId)}/internal-roles`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    end: (profileId: string, roleId: string) =>
      request<{ ok: true; ended_at: string; request_id: string }>(
        `/api/profiles/${encodeURIComponent(profileId)}/internal-roles/${encodeURIComponent(roleId)}`,
        { method: 'DELETE' },
      ),
  },
  visibilityScopes: {
    revoke: (profileId: string, scopeId: string) =>
      request<{ ok: true; revoked_at: string; request_id: string }>(
        `/api/profiles/${encodeURIComponent(profileId)}/visibility-scopes/${encodeURIComponent(scopeId)}`,
        { method: 'DELETE' },
      ),
    // Grant intentionally not exposed in 010.E.4 — CANONICAL_SCOPE_KINDS empty;
    // future Phase D / E patches extend and unlock the control.
  },
  projectProfiles: (projectId: string) =>
    request<ProjectProfilesResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/profiles`,
    ),
}
